<template>
  <div class="campanhas-inteligentes-container">
    <!-- Header -->
    <div class="page-header mb-4">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h2 class="page-title mb-1">
            <feather-icon
              icon="ZapIcon"
              size="28"
              class="text-primary mr-2"
            />
            Campanhas Inteligentes
          </h2>
          <p class="text-muted mb-0">
            Automatize suas campanhas com inteligência artificial
          </p>
        </div>
      </div>
    </div>

    <!-- Seleção de Loja -->
    <b-card
      v-if="lojas.length > 1"
      class="loja-selector mb-4"
    >
      <h6 class="mb-3">
        <feather-icon
          icon="StoreIcon"
          size="18"
          class="mr-2"
        />
        Selecionar Loja
      </h6>
      <b-form-select
        v-model="lojaSelecionada"
        :options="lojasOptions"
        size="sm"
        @change="onLojaSelecionada"
      />
      <small class="text-muted">Escolha a loja para configurar as campanhas inteligentes</small>
    </b-card>

    <!-- Grid de Opções de Campanhas -->
    <div class="campaigns-grid">
      <div
        v-for="(opcao, index) in opcoesCampanha"
        :key="index"
        class="campaign-card"
        :class="{ 'campaign-card-disabled': !opcao.ativo }"
      >
        <div class="campaign-card-inner">
          <!-- Badge "Em Breve" -->
          <b-badge
            v-if="!opcao.ativo"
            variant="secondary"
            class="campaign-badge"
          >
            Em breve
          </b-badge>

          <!-- Ícone -->
          <div
            class="campaign-icon"
            :class="{ 'icon-disabled': !opcao.ativo }"
          >
            <feather-icon
              :icon="opcao.icon"
              size="48"
            />
          </div>

          <!-- Conteúdo -->
          <div class="campaign-content">
            <h4 class="campaign-title">
              {{ opcao.titulo }}
            </h4>
            <p class="campaign-description">
              {{ opcao.descricao }}
            </p>
          </div>

          <!-- Footer -->
          <div class="campaign-footer">
            <div
              v-if="opcao.ativo"
              class="d-flex gap-2"
            >
              <b-button
                variant="primary"
                size="sm"
                class="flex-fill"
                @click.stop="configurarCampanha(opcao)"
              >
                <feather-icon
                  icon="SettingsIcon"
                  size="14"
                  class="mr-50"
                />
                Configurar
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                @click.stop="verMetricas(opcao)"
              >
                <feather-icon
                  icon="BarChartIcon"
                  size="14"
                  class="mr-50"
                />
                Ver Métricas
              </b-button>
            </div>
            <div
              v-else
              class="text-center text-muted"
            >
              <small><em>Funcionalidade em desenvolvimento</em></small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BFormSelect,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import stores from '@/functions/basics/stores'

export default {
  name: 'CampanhasInteligentes',
  components: {
    BBadge,
    BButton,
    BCard,
    BFormSelect,
    FeatherIcon,
  },
  data() {
    return {
      opcoesCampanha: [
        {
          titulo: 'Recomendação de Produtos',
          descricao: 'Sugira produtos com base no histórico de compras e perfil de clientes semelhantes usando IA.',
          icon: 'TrendingUpIcon',
          ativo: true,
          route: 'campaign-ia-recomendacao',
        },
        {
          titulo: 'Queima de Estoque',
          descricao: 'Divulgue produtos com estoque alto ou parados há muito tempo para os clientes mais prováveis de comprar.',
          icon: 'PackageIcon',
          ativo: false,
        },
        {
          titulo: 'Cronológica',
          descricao: 'Envie mensagens em sequência, com base na data crescente de compra do cliente.',
          icon: 'ClockIcon',
          ativo: false,
        },
        {
          titulo: 'Aniversariantes',
          descricao: 'Envie mensagens para aniversariantes na data e/ou que compraram próximo ao aniversário.',
          icon: 'GiftIcon',
          ativo: true,
          route: 'campaign-ia-aniversario',
        },
        {
          titulo: 'Recuperação de Clientes',
          descricao: 'Reconquiste clientes inativos com ofertas personalizadas baseadas em seu histórico.',
          icon: 'UserPlusIcon',
          ativo: false,
        },
      ],
      lojas: [],
      lojaSelecionada: null,
    }
  },
  computed: {
    lojasOptions() {
      return this.lojas.map(loja => ({
        value: loja.uuid || loja.id,
        text: loja.nome || loja.name || loja.store_name || 'Loja sem nome',
      }))
    },
  },
  async created() {
    await this.carregarLojas()
  },
  methods: {
    async carregarLojas() {
      try {
        const lojas = await stores()
        console.log('Lojas carregadas:', lojas) // Debug
        this.lojas = lojas || []

        // Se houver lojas, selecionar a primeira
        if (this.lojas.length > 0) {
          this.lojaSelecionada = this.lojaSelecionada || (this.lojas[0].uuid || this.lojas[0].id)
        }
      } catch (error) {
        console.error('Erro ao carregar lojas:', error)
        this.$bvToast.toast('Erro ao carregar lojas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    onLojaSelecionada() {
      // Salvar loja selecionada no localStorage para uso em outras páginas
      if (this.lojaSelecionada) {
        localStorage.setItem('lojaSelecionada', this.lojaSelecionada)

        // Salvar também o nome da loja
        const loja = this.lojas.find(l => (l.uuid || l.id) === this.lojaSelecionada)
        if (loja) {
          const nomeLoja = loja.nome || loja.name || loja.store_name || 'Loja sem nome'
          localStorage.setItem('nomeLojaSelecionada', nomeLoja)
        }

        this.$bvToast.toast('Loja selecionada com sucesso!', {
          title: 'Loja Selecionada',
          variant: 'success',
          solid: true,
        })
      }
    },

    configurarCampanha(opcao) {
      if (!this.lojaSelecionada) {
        this.$bvToast.toast('Selecione uma loja primeiro', {
          title: 'Loja Obrigatória',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (opcao.route) {
        // Buscar o nome da loja
        const loja = this.lojas.find(l => (l.uuid || l.id) === this.lojaSelecionada)
        const nomeLoja = loja ? (loja.nome || loja.name || loja.store_name || 'Loja sem nome') : ''

        this.$router.push({
          name: opcao.route,
          query: {
            loja: this.lojaSelecionada,
            nomeLoja,
          },
        })
      }
    },

    verMetricas(opcao) {
      if (!this.lojaSelecionada) {
        this.$bvToast.toast('Selecione uma loja primeiro', {
          title: 'Loja Obrigatória',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (opcao.route) {
        // Buscar o nome da loja
        const loja = this.lojas.find(l => (l.uuid || l.id) === this.lojaSelecionada)
        const nomeLoja = loja ? (loja.nome || loja.name || loja.store_name || 'Loja sem nome') : ''

        // Para "Recomendação de Produtos", redirecionar para a página de métricas
        if (opcao.titulo === 'Recomendação de Produtos') {
          this.$router.push({
            name: 'campaign-ia-metricas',
            query: {
              loja: this.lojaSelecionada,
              nomeLoja,
            },
          })
        } else if (opcao.titulo === 'Aniversariantes') {
          // Para "Aniversariantes", redirecionar para a página de métricas dedicada
          this.$router.push({
            name: 'campaign-ia-aniversario-metricas',
            query: {
              loja: this.lojaSelecionada,
              nomeLoja,
            },
          })
        } else {
          this.$bvToast.toast(
            'Métricas disponíveis apenas para Recomendação de Produtos e Aniversariantes',
            {
              title: 'Métricas',
              variant: 'info',
              solid: true,
            },
          )
        }
      }
    },
  },
}
</script>

<style scoped>
.campanhas-inteligentes-container {
  padding: 2rem;
  min-height: 100vh;
  background: #f8f9fa;
}

/* Header */
.page-header {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
}

/* Grid de Campanhas */
.campaigns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

/* Card de Campanha */
.campaign-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.campaign-card:hover:not(.campaign-card-disabled) {
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.15);
  border-color: #9A3092 ;
}

.campaign-card-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.campaign-card-disabled:hover {
  transform: none;
}

.campaign-card-inner {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 280px;
  position: relative;
}

/* Badge "Em Breve" */
.campaign-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.75rem;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  z-index: 10;
}

/* Ícone */
.campaign-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #9A3092  0%, #61185b  100%);
  border-radius: 50%;
  color: white;
  transition: all 0.3s ease;
}

.campaign-card:hover:not(.campaign-card-disabled) .campaign-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 8px 16px rgba(115, 103, 240, 0.3);
}

.icon-disabled {
  background: #e0e0e0;
  color: #999;
}

/* Conteúdo */
.campaign-content {
  flex: 1;
  text-align: center;
  margin-bottom: 1.5rem;
}

.campaign-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.campaign-description {
  font-size: 0.9rem;
  color: #6e6b7b;
  line-height: 1.6;
  margin-bottom: 0;
}

/* Footer */
.campaign-footer {
  margin-top: auto;
}

.campaign-footer small {
  font-size: 0.8rem;
}

.gap-2 {
  gap: 0.5rem;
}

/* Loja Selector */
.loja-selector {
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

/* Responsividade */
@media (max-width: 1200px) {
  .campaigns-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 768px) {
  .campanhas-inteligentes-container {
    padding: 1rem;
  }

  .page-header {
    padding: 1.5rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .campaigns-grid {
    grid-template-columns: 1fr;
  }

  .campaign-card-inner {
    min-height: 240px;
  }

  .campaign-icon {
    width: 64px;
    height: 64px;
  }

  .campaign-icon svg {
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 576px) {
  .page-title {
    font-size: 1.25rem;
  }

  .campaign-title {
    font-size: 1.1rem;
  }

  .campaign-description {
    font-size: 0.85rem;
  }
}
</style>
